import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { isAdminPortal } from './utils/portalMode';
import { AuthProvider, useAuth } from './context/AuthContext';

// Shared pages
import LoginPage from './pages/auth/LoginPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import AutoLoginPage from './pages/auth/AutoLoginPage';

// Admin portal pages
import AdminLayout from './components/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminTenantsPage from './pages/admin/AdminTenantsPage';
import AdminServersPage from './pages/admin/AdminServersPage';

// Tenant portal pages
import Layout from './components/Layout';
import DashboardPage from './pages/dashboard/DashboardPage';
import AgenciesPage from './pages/agencies/AgenciesPage';
import StationsPage from './pages/stations/StationsPage';
import UnitsPage from './pages/units/UnitsPage';
import AgentsPage from './pages/agents/AgentsPage';
import RoutingPage from './pages/routing/RoutingPage';
import AlertsPage from './pages/alerts/AlertsPage';
import ManualAlertPage from './pages/manual-alert/ManualAlertPage';
import TonesPage from './pages/tones/TonesPage';
import PronunciationPage from './pages/pronunciation/PronunciationPage';
import TTSSettingsPage from './pages/settings/TTSSettingsPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import SystemLogPage from './pages/system-log/SystemLogPage';
import UsersPage from './pages/users/UsersPage';
import BrandingPage from './pages/branding/BrandingPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token, mustChangePassword } = useAuth();
  if (!token) return <Navigate to="/login" />;
  if (mustChangePassword) return <Navigate to="/change-password" />;
  return <>{children}</>;
}

function AdminPortalRoutes() {
  const { token, isSuperadmin, mustChangePassword } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/change-password" element={token ? <ChangePasswordPage /> : <Navigate to="/login" />} />
      <Route
        path="/*"
        element={
          (() => {
            if (!token) return <Navigate to="/login" />;
            if (mustChangePassword) return <Navigate to="/change-password" />;
            if (!isSuperadmin) return <Navigate to="/login" />;
            return (
              <AdminLayout>
                <Routes>
                  <Route path="/" element={<AdminDashboardPage />} />
                  <Route path="/tenants" element={<AdminTenantsPage />} />
                  <Route path="/servers" element={<AdminServersPage />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </AdminLayout>
            );
          })()
        }
      />
    </Routes>
  );
}

function TenantPortalRoutes() {
  const { token } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auto-login" element={<AutoLoginPage />} />
      <Route path="/change-password" element={token ? <ChangePasswordPage /> : <Navigate to="/login" />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/agencies" element={<AgenciesPage />} />
                <Route path="/stations" element={<StationsPage />} />
                <Route path="/units" element={<UnitsPage />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/routing" element={<RoutingPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/manual-alert" element={<ManualAlertPage />} />
                <Route path="/tones" element={<TonesPage />} />
                <Route path="/pronunciation" element={<PronunciationPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/branding" element={<BrandingPage />} />
                <Route path="/settings/tts" element={<TTSSettingsPage />} />
                <Route path="/settings/notifications" element={<NotificationsPage />} />
                <Route path="/settings/system-log" element={<SystemLogPage />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function AppRoutes() {
  if (isAdminPortal()) {
    return <AdminPortalRoutes />;
  }
  return <TenantPortalRoutes />;
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
